import {css} from '@mui/material';
import {useFormatters} from '@wallaby-ui/document';
import {Column} from 'material-table';
import * as React from 'react';
import {useMemo} from 'react';

import {UzisSimpleTable, ValueCellWithCopy} from './UzisReportTableCommon';
import {UzisReportVIIIResult} from './uzisreporttypes';

type Props = {
  data: UzisReportVIIIResult | undefined;
  isLoading: boolean | undefined;
  storageIdPrefix: string;
};

type TableRow = {
  riskFactor: string;
  rowNumber: string;
  count: number;
};

export const UzisReportTableVIII: React.FC<Props> = ({data, isLoading = true, storageIdPrefix}) => {
  const {formatFloat, t} = useFormatters();

  const tableData: TableRow[] = useMemo(() => {
    if (!data) return [];
    return [
      {riskFactor: 'a', rowNumber: 'b', count: 1},
      {riskFactor: t('reports.uzis.tableVIII.risk.polypharmacy'), rowNumber: '81', count: data.polypharmacy},
      {riskFactor: t('reports.uzis.tableVIII.risk.nti'), rowNumber: '82', count: data.nti},
      {riskFactor: t('reports.uzis.tableVIII.risk.hip'), rowNumber: '83', count: data.hip},
      {riskFactor: t('reports.uzis.tableVIII.risk.renal'), rowNumber: '84', count: data.renalInsufficiency},
      {riskFactor: t('reports.uzis.tableVIII.risk.hepatic'), rowNumber: '85', count: data.hepaticInsufficiency},
      {riskFactor: t('reports.uzis.tableVIII.risk.significantChanges'), rowNumber: '86', count: data.significantChanges},
      {riskFactor: t('reports.uzis.tableVIII.risk.intensiveCare'), rowNumber: '87', count: data.intensiveCare},
      {
        riskFactor: t('reports.uzis.tableVIII.risk.diabetes'),
        rowNumber: '88',
        count: data.diabetesMellitus,
      },
      {riskFactor: t('reports.uzis.tableVIII.risk.epilepsy'), rowNumber: '89', count: data.epilepsy},
      {riskFactor: t('reports.uzis.tableVIII.risk.fibrillation'), rowNumber: '90', count: data.fibrillation},
      {riskFactor: t('reports.uzis.tableVIII.risk.tumor'), rowNumber: '91', count: data.tumor},
      {
        riskFactor: t('reports.uzis.tableVIII.risk.corticosteroids'),
        rowNumber: '92',
        count: data.corticosteroids,
      },
      {riskFactor: t('reports.uzis.tableVIII.risk.parkinson'), rowNumber: '93', count: data.parkinson},
    ];
  }, [data, t]);

  const columns: Column<TableRow>[] = useMemo(
    () => [
      {
        field: 'riskFactor',
        title: '',
        render: (rowData) => <span style={{fontWeight: 500}}>{rowData.riskFactor}</span>,
        sorting: false,
        cellStyle: {minWidth: '450px', textAlign: 'left'},
        headerStyle: {minWidth: '450px', textAlign: 'center'},
      },
      {
        field: 'rowNumber',
        title: t('reports.uzis.common.rowNumber'),
        render: (rowData) => <span style={{fontWeight: 500}}>{rowData.rowNumber}</span>,
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '150px', color: '#9e9e9e'},
        headerStyle: {textAlign: 'center', minWidth: '150px'},
      },
      {
        field: 'count',
        title: t('reports.uzis.common.count'),
        render: (rowData) => <ValueCellWithCopy value={rowData.count} formatInteger={formatFloat} />,
        sorting: false,
        cellStyle: {textAlign: 'center', width: '150px'},
        headerStyle: {textAlign: 'center', width: '150px'},
      },
    ],
    [formatFloat, t]
  );

  return (
    <UzisSimpleTable<TableRow>
      storageScrollKey={`${storageIdPrefix}.tableVIII.scroll`}
      headerLabel='reports.uzis.tableVIII.title'
      columns={columns}
      data={tableData}
      isLoading={isLoading}
      options={{
        rowStyle: (_rowData, index) => {
          return {
            color: index === 0 ? '#9e9e9e' : undefined,
            textAlign: index === 0 ? 'center' : 'left',
          };
        },
      }}
      tableAutoWidthStyle={css`
        & table tbody tr:nth-child(1) td:nth-child(1) {
          text-align: center;
        }
      `}
    />
  );
};
